import { useRef, useState, useEffect } from "react";
import { StaticMathField, addStyles } from "react-mathquill";

addStyles();

function getVisualLength(latex) {
  // "\ " is 2 chars in string but 1 char visually
  // "\frac" is 5 chars but renders as a fraction symbol
  // Count visual characters more accurately
  let visual = 0;
  let i = 0;
  while (i < latex.length) {
    if (latex[i] === "\\") {
      if (latex[i + 1] === " ") {
        // "\ " = 1 visible space
        visual += 1;
        i += 2;
      } else {
        // LaTeX command like \frac, \pi, etc — skip to end of command
        let cmd = "\\";
        i++;
        while (i < latex.length && /[a-zA-Z]/.test(latex[i])) {
          cmd += latex[i];
          i++;
        }
        // Most commands render as 1-3 visible characters
        if (cmd === "\\frac") visual += 1;
        else if (cmd === "\\sqrt") visual += 1;
        else visual += 2; // generic command ~2 chars wide
      }
    } else if (latex[i] === "{" || latex[i] === "}") {
      // Braces are invisible
      i++;
    } else if (latex[i] === "^" || latex[i] === "_") {
      // Superscript/subscript markers — invisible, content is smaller
      visual += 0;
      i++;
    } else {
      visual += 1;
      i++;
    }
  }
  return visual;
}

function splitLatexToLines(latex, maxVisualChars) {
  if (!latex || maxVisualChars <= 0) return [latex];
  if (getVisualLength(latex) <= maxVisualChars) return [latex];

  const lines = [];
  let remaining = latex;

  while (getVisualLength(remaining) > maxVisualChars) {
    // Find all "\ " positions
    let bestBreak = -1;
    let visualSoFar = 0;
    let i = 0;

    while (i < remaining.length && visualSoFar <= maxVisualChars) {
      if (remaining[i] === "\\" && remaining[i + 1] === " ") {
        if (visualSoFar <= maxVisualChars) {
          bestBreak = i + 2; // break after "\ "
        }
        visualSoFar += 1;
        i += 2;
      } else if (remaining[i] === "\\" ) {
        let cmd = "\\";
        i++;
        while (i < remaining.length && /[a-zA-Z]/.test(remaining[i])) {
          cmd += remaining[i];
          i++;
        }
        if (cmd === "\\frac" || cmd === "\\sqrt") visualSoFar += 1;
        else visualSoFar += 2;
      } else if (remaining[i] === "{" || remaining[i] === "}") {
        i++;
      } else if (remaining[i] === "^" || remaining[i] === "_") {
        i++;
      } else {
        visualSoFar += 1;
        i++;
      }
    }

    if (bestBreak <= 0) {
      // No good break — take everything up to visual limit
      lines.push(remaining);
      remaining = "";
    } else {
      lines.push(remaining.slice(0, bestBreak));
      remaining = remaining.slice(bestBreak);
    }
  }

  if (remaining) lines.push(remaining);
  return lines;
}

export default function StaticMathDisplay({ item }) {
  const { res } = item;
  const fontSize = res?.size || "23px";
  const isMath = res?.math === true;
  const text = res?.text || "";
  const containerRef = useRef(null);
  const [lines, setLines] = useState([text]);

  useEffect(() => {
    if (!isMath || !text || !containerRef.current) return;

    const recalc = () => {
      const width = containerRef.current?.offsetWidth;
      if (!width) return;

      const usableWidth = width - 32;
      const fontPx = parseInt(fontSize) || 23;
      const avgCharWidth = fontPx * 0.55;
      const maxVisualChars = Math.floor(usableWidth / avgCharWidth);

      setLines(splitLatexToLines(text, maxVisualChars));
    };

    recalc();

    const observer = new ResizeObserver(recalc);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [text, fontSize, isMath]);

  if (!text) return null;

  if (isMath) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 16px",
          borderRadius: 10,
          background: "#ffffff",
          border: "1.5px solid #e8e8e8",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "visible",
        }}
      >
        <style>{`
          .static-math-display {
            pointer-events: none !important;
            user-select: none !important;
            -webkit-user-select: none !important;
          }
          .static-math-display .mq-cursor { display: none !important; }
          .static-math-display .mq-selection { display: none !important; }
        `}</style>
        <div
          className="static-math-display"
          style={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {lines.map((line, i) => (
            <div key={`${i}-${line.slice(0, 10)}`}>
              <StaticMathField
                style={{
                  fontSize,
                  fontFamily: "'Georgia', serif",
                  display: "block",
                  minHeight: 24,
                  border: "none",
                }}
              >
                {line}
              </StaticMathField>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "12px 16px",
        borderRadius: 10,
        background: "#ffffff",
        border: "1.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize,
          lineHeight: 1.6,
          color: "#2d2d2d",
          fontFamily: "'Georgia', serif",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {text}
      </p>
    </div>
  );
}